import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { omit } from 'lodash'

import { Portal } from '@revolut-internal/ui-kit'

import { FCWithChildren, PartialBy, UUID } from 'types'
import { uuid } from 'utils/uuid'

import { ToastProps } from './Toast'
import { ToastsStack, ToastWithTransition } from './ToastsStack'

type OptionsObject = PartialBy<ToastProps, 'timeout' | 'onDisappear'> & { id?: string }

export type AddToastOptions = OptionsObject | string

type AddToastFn = {
  (message: string): string
  (options: AddToastOptions): string
}

type ToastsContextType = {
  addToast: AddToastFn
  addSuccessToast: AddToastFn
  removeToast: (toastId: UUID) => void
}

export const ToastsContext = createContext({} as ToastsContextType)

export const useToasts = () => {
  const context = useContext(ToastsContext)

  if (!context) {
    throw new Error('ToastsContext cannot be used outside ToastsContextProvider')
  }
  return context
}

const DEFAULT_TIMEOUT = 4000

const getOptions = (passedOptions: AddToastOptions) =>
  typeof passedOptions === 'string'
    ? ({ label: passedOptions } as OptionsObject)
    : passedOptions

export const ToastsProvider: FCWithChildren = ({ children }) => {
  const [toasts, setToasts] = useState<Record<string, ToastWithTransition>>({})

  // first animate and then remove a toast from the state on animation end
  const removeToast = useCallback((toastId: UUID) => {
    const deleteToast = () => setToasts(prevToasts => omit(prevToasts, toastId))

    setToasts(prevToasts => {
      const toast = prevToasts[toastId]

      const updatedToast = {
        ...toast,
        transition: {
          in: false,
          onExited: deleteToast,
        },
      }

      return {
        ...prevToasts,
        [toastId]: updatedToast,
      }
    })
  }, [])

  const addToast: AddToastFn = useCallback(
    passedOptions => {
      const options = getOptions(passedOptions)

      const toastId = options.id ?? uuid()
      const timeout = options.timeout ?? DEFAULT_TIMEOUT

      const onDisappear = () => {
        removeToast(toastId)
        options.onDisappear?.()
      }

      const toast = {
        ...options,
        id: toastId,
        timeout,
        transition: {
          in: true,
        },
        onDisappear,
      }

      setToasts(prevToasts => ({
        ...prevToasts,
        [toastId]: toast,
      }))

      return toastId
    },
    [removeToast],
  )

  const addSuccessToast: AddToastFn = useCallback(
    passedOptions => {
      const options = getOptions(passedOptions)

      return addToast({ ...options, icon: { name: 'Check', size: 24 } })
    },
    [addToast],
  )

  const toastsArray = useMemo(() => Object.values(toasts), [toasts])

  const value = useMemo(
    () => ({
      addToast,
      addSuccessToast,
      removeToast,
    }),
    [addToast, addSuccessToast, removeToast],
  )

  return (
    <ToastsContext.Provider value={value}>
      {children}
      <Portal>
        <ToastsStack toasts={toastsArray} />
      </Portal>
    </ToastsContext.Provider>
  )
}
